<script setup lang="ts">
import { toRefs } from "vue";
import type { Postcard } from "@/rvapi";
import config from "@/config";
import { utc2local } from "@/utils";

const props = defineProps<{
  postcard: Postcard;
  history: boolean;
  expired: boolean;
}>();
const emit = defineEmits<{
  (e: "checked", cId: string, rId: string, value: boolean): void;
}>();

const { postcard, expired, history } = toRefs(props);

const trackingHref =
  "http://webvcs.ipostnord.com/glp/riktiga_vykort.asp?seq_no=";
</script>

<template>
  <div class="postcard-details">
    <dl class="postcard-info">
      <dt v-if="!history">Created date</dt>
      <dd v-if="!history">{{ utc2local(postcard.createdDate) }}</dd>
      <dt v-if="!history">Customer reference</dt>
      <dd v-if="!history">{{ postcard.customerReference }}</dd>
      <dt>Format</dt>
      <dd>{{ postcard.format }}</dd>
    </dl>
    <a
      v-if="!history"
      :href="postcard.cardContent?.frontImageUrl"
      class="front-image"
    >
      <img
        :src="postcard.cardContent?.frontImageUrl"
        class="full-width rounded"
      />
    </a>
    <a
      v-if="!history"
      :href="postcard.cardContent?.backImageUrl"
      class="front-image"
    >
      <img
        :src="postcard.cardContent?.backImageUrl"
        class="full-width rounded"
      />
    </a>
  </div>
  <div v-if="!history" class="zip">
    <h3>
      Postcard Zipfiles <span>({{ postcard.batchIds.length }})</span>
    </h3>

    <ul>
      <li v-for="id in postcard.batchIds" :key="id">
        <a :href="`${config.zipBucket}/${postcard.key}/${id}.zip`">
          {{ id }}
        </a>
      </li>
    </ul>
  </div>
  <div class="receivers">
    <h3>
      Receivers <span>({{ postcard.receivers.length }})</span>
    </h3>

    <pn-table bordered="true" striped="true">
      <table>
        <thead>
          <tr>
            <th v-if="!history">Status</th>
            <th v-if="!history">Tracking Id</th>
            <th v-if="!history">Batch Id</th>
            <th v-if="!history">Name</th>
            <th v-if="!history">Name2</th>
            <th v-if="!history">Address</th>
            <th v-if="!history">City</th>
            <th>Postal code</th>
            <th>Country</th>
            <th v-if="!history">Zip</th>
            <th v-if="!history && !expired">Resend</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in postcard.receivers" :key="r._id">
            <td v-if="!history">{{ r.status }}</td>
            <td v-if="!history">
              <a
                v-if="r.trackingId"
                :href="trackingHref + r.trackingId"
                target="_blank"
              >
                {{ r.trackingId }}
              </a>
              <br />
              <a
                :href="`https://www.postnord.se/vara-verktyg/sok-postnummer-och-adress?countryCode=${r.countryCode}&query=${r.postalCode}`"
                target="_blank"
                rel="noopener"
              >
                (PN)
              </a>
              <a
                :href="`https://postnummerservice.se/adressoekning/?street=${r.address}&postalcode=${r.postalCode}&locality=&submit=&external=0`"
                target="_blank"
                rel="noopener"
              >
                (PS)
              </a>
            </td>
            <td v-if="!history">{{ r.batchId }}</td>
            <td v-if="!history">{{ r.name }}</td>
            <td v-if="!history">{{ r.name2 }}</td>
            <td v-if="!history">{{ r.address }}</td>
            <td v-if="!history">{{ r.city }}</td>
            <td>{{ r.postalCode }}</td>
            <td>{{ r.country }} ({{ r.countryCode }})</td>
            <td v-if="!history">
              <a
                v-if="['SENT', 'PRINTED'].includes(r.status)"
                :href="`${config.zipBucket}/${postcard.key}/${r.batchId}.zip`"
                >link</a
              >
            </td>
            <td class="checkbox" v-if="!history && !expired">
              <label>
                <input
                  type="checkbox"
                  @change="
                    (e: any) => {
                      emit('checked', postcard._id, r._id, e.target.checked);
                    }
                  "
                />
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </pn-table>
  </div>
</template>

<style lang="scss" scoped>
$gray50: #f3f2f2;

.resend {
  text-align: end;
}

.postcard-details {
  display: grid;
  grid-template-columns: 1fr 1fr 3fr;
  grid-auto-flow: row dense;
  column-gap: 2rem;
}

.postcard {
  background-color: white;
  padding: 1rem;
  border-radius: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border: 1px solid $gray50;

  img {
    border: 1px dashed gray;
  }

  .receivers {
    grid-column: 1 / span 2;
  }

  .checkbox {
    padding: 0px;

    label {
      margin: auto;
      padding: 15px;
      display: block;
    }
  }
}

.full-width {
  max-width: 300px;
  max-height: 300px;
}

.rounded {
  border-radius: 1rem;
}
</style>
